import { useMemo } from 'react';
import { FormattedMessage } from 'react-intl';

import type { ResourceImpacts } from '@/lib/queries/resource';
import type { HandledCloudResource } from '@/lib/types/resourceDefs';
import CoverageTable from '@/routes/_root/coverage/-components/CoverageTable';

import { DateRangeDisplay } from '../DateRangeDisplay';

interface ResourceDetailsProps {
  hashedID: string;
  resources: HandledCloudResource[];
  historyImpacts: ResourceImpacts | undefined;
  from?: string;
  to?: string;
}

export const ResourceDetails = ({
  hashedID,
  resources,
  historyImpacts,
  from,
  to,
}: ResourceDetailsProps): JSX.Element => {
  const { impacts, resourceDetails } = useMemo(() => {
    const details = resources.find(resource => resource.hashedID === hashedID);

    if (!details) {
      return {
        impacts: [],
        impactedBy: [],
        resourceDetails: null,
      };
    }

    const resourceImpacts = details.impacts.map(impact => impact.hashedID);
    const resourceImpactedBy = details.impactedBy.map(
      impact => impact.hashedID,
    );

    return {
      impacts: [
        ...resources
          .filter(resource => resourceImpacts.includes(resource.hashedID))
          .map(r => ({
            ...r,
            impactType: 'downstream' as const,
            updates: historyImpacts?.downstream[r.hashedID]?.timestamps ?? [],
          })),
        ...resources
          .filter(resource => resourceImpactedBy.includes(resource.hashedID))
          .map(r => ({
            ...r,
            impactType: 'upstream' as const,
            updates: historyImpacts?.upstream[r.hashedID]?.timestamps ?? [],
          })),
      ].filter(
        ({ updates }) => historyImpacts === undefined || updates.length > 0,
      ),
      resourceDetails: details,
    };
  }, [resources, hashedID, historyImpacts]);

  if (!resourceDetails) {
    return <></>;
  }

  return (
    <div className="mb-6 w-full">
      <div className="space-y-12 mt-8">
        <div>
          <div className="flex flex-row items-center mb-4 space-x-4">
            <h2 className="text-2xl">
              <FormattedMessage id="coverage.details.impacts" />
            </h2>
            <DateRangeDisplay from={from} to={to} prefix="Updated" />
          </div>
          <CoverageTable
            resources={impacts}
            from={from}
            to={to}
            showImpactType
            noResultsMessage="resource.updatesPeriod.impacts.noResults"
          />
        </div>
      </div>
    </div>
  );
};
